import {useEffect, useRef, useState} from "react";
import {isCancelledError, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {useSnackbar} from "notistack";
import {PerformanceTotalizer} from "../PerformanceTotalizer.types";
import {MAX_PRESERVED_REFRESH_FAILURES} from "../../PerformanceRefreshPolicy";

const API_URL = "api/power-station/performance-analysis/totalizers";

export const PERFORMANCE_TOTALIZERS_QUERY_KEY_ROOT = "performance-totalizers";

export const buildPerformanceTotalizersQueryKey = (
    powerStationIds: number[],
    startDateTime: string | null,
    endDateTime: string | null
) => [PERFORMANCE_TOTALIZERS_QUERY_KEY_ROOT, powerStationIds, startDateTime, endDateTime] as const;

export const serializePerformanceTotalizersQueryKey = (
    powerStationIds: number[],
    startDateTime: string | null,
    endDateTime: string | null
): string => JSON.stringify(buildPerformanceTotalizersQueryKey(powerStationIds, startDateTime, endDateTime));

export const hasValidPerformanceTotalizersSnapshot = (
    data: PerformanceTotalizer[] | undefined
): boolean => Array.isArray(data) && data.length > 0;

export const isPerformanceTotalizersPreserveExpired = (consecutiveAutomaticFailures: number): boolean =>
    consecutiveAutomaticFailures > MAX_PRESERVED_REFRESH_FAILURES;

/** Notifica só na falha que troca o dado preservado por "Dados não encontrados!". */
export const shouldNotifyPerformanceTotalizersPreserveExpired = (
    consecutiveAutomaticFailures: number,
    cachedData: PerformanceTotalizer[] | undefined
): boolean =>
    isPerformanceTotalizersPreserveExpired(consecutiveAutomaticFailures) &&
    !isPerformanceTotalizersPreserveExpired(consecutiveAutomaticFailures - 1) &&
    hasValidPerformanceTotalizersSnapshot(cachedData);

type PendingAutomaticRefetch = {
    serializedKey: string;
};

let pendingAutomaticTotalizersRefetch: PendingAutomaticRefetch | null = null;

export const markPerformanceTotalizersAutomaticRefetch = (
    powerStationIds: number[],
    startDateTime: string | null,
    endDateTime: string | null
) => {
    pendingAutomaticTotalizersRefetch = {
        serializedKey: serializePerformanceTotalizersQueryKey(powerStationIds, startDateTime, endDateTime),
    };
};

/** @internal test helper */
export const resetPerformanceTotalizersAutomaticRefetchMarker = () => {
    pendingAutomaticTotalizersRefetch = null;
};

/** @internal test helper */
export const getPendingPerformanceTotalizersAutomaticRefetch = () => pendingAutomaticTotalizersRefetch;

const clearPendingAutomaticRefetchForKey = (serializedKey: string) => {
    if (pendingAutomaticTotalizersRefetch?.serializedKey === serializedKey) {
        pendingAutomaticTotalizersRefetch = null;
    }
};

const consumePendingAutomaticRefetch = (serializedKey: string): boolean => {
    if (!pendingAutomaticTotalizersRefetch) {
        return false;
    }
    if (pendingAutomaticTotalizersRefetch.serializedKey !== serializedKey) {
        return false;
    }
    pendingAutomaticTotalizersRefetch = null;
    return true;
};

const isRefreshCancellation = (error: unknown): boolean => {
    if (isCancelledError(error)) {
        return true;
    }
    return typeof axios.isCancel === "function" && axios.isCancel(error);
};

const createChromeTransportError = (): Error =>
    Object.assign(new Error("Network Error"), {code: "ERR_NETWORK"});

const getPerformanceTotalizersRetryDelay = (
    failureCount: number,
    serializedKey: string
): number => {
    if (pendingAutomaticTotalizersRefetch?.serializedKey === serializedKey) {
        return 0;
    }

    // Mantém o backoff padrão do React Query fora dos ciclos automáticos.
    return Math.min(1000 * 2 ** failureCount, 30000);
};

/** Limpa a marca automática quando o refetch não encontrou consulta correspondente. */
export const releasePerformanceTotalizersAutomaticRefetchMarker = (
    powerStationIds: number[],
    startDateTime: string | null,
    endDateTime: string | null,
    hadMatchingActiveQuery: boolean
) => {
    if (hadMatchingActiveQuery) {
        return;
    }
    clearPendingAutomaticRefetchForKey(
        serializePerformanceTotalizersQueryKey(powerStationIds, startDateTime, endDateTime)
    );
};

const fetchPerformanceTotalizers = async (
    powerStationIds: number[],
    startDateTime: string,
    endDateTime: string
): Promise<PerformanceTotalizer[]> => {
    try {
        const response = await axios.post<{ data: PerformanceTotalizer[] }>(
            API_URL,
            {powerStationIds},
            {
                params: {startDateTime, endDateTime},
                timeout: 30000,
            }
        );

        // Axios 0.27 settle() resolves XHR status 0 as success (`!response.status`).
        // Chrome DevTools blocking / ERR_BLOCKED_BY_CLIENT finishes onloadend with status 0.
        if (response.status === 0) {
            return Promise.reject(createChromeTransportError());
        }

        return response.data.data;
    } catch (error) {
        console.error("Erro ao buscar totalizadores de performance:", error);
        return Promise.reject(error);
    }
};

export const usePerformanceTotalizersQuery = (
    powerStationIds: number[],
    startDateTime: string | null,
    endDateTime: string | null
) => {
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const consecutiveFailuresRef = useRef(0);
    const [consecutiveAutomaticFailures, setConsecutiveAutomaticFailures] = useState(0);

    const queryKey = buildPerformanceTotalizersQueryKey(powerStationIds, startDateTime, endDateTime);
    const serializedQueryKey = serializePerformanceTotalizersQueryKey(powerStationIds, startDateTime, endDateTime);

    useEffect(() => {
        if (
            pendingAutomaticTotalizersRefetch &&
            pendingAutomaticTotalizersRefetch.serializedKey !== serializedQueryKey
        ) {
            pendingAutomaticTotalizersRefetch = null;
        }
        consecutiveFailuresRef.current = 0;
        setConsecutiveAutomaticFailures(0);
    }, [serializedQueryKey]);

    useEffect(
        () => () => {
            clearPendingAutomaticRefetchForKey(serializedQueryKey);
        },
        [serializedQueryKey]
    );

    const query = useQuery({
        queryKey,
        queryFn: () => fetchPerformanceTotalizers(powerStationIds, startDateTime as string, endDateTime as string),
        enabled: !!startDateTime && !!endDateTime && powerStationIds.length > 0,
        keepPreviousData: true,
        // As retentativas continuam pertencendo ao mesmo ciclo automático, mas sem
        // backoff para não deixar o totalizador atrasado em relação aos cartões.
        retryDelay: (failureCount) => getPerformanceTotalizersRetryDelay(failureCount, serializedQueryKey),
        onSuccess: () => {
            consecutiveFailuresRef.current = 0;
            setConsecutiveAutomaticFailures(0);
            clearPendingAutomaticRefetchForKey(serializedQueryKey);
        },
        onError: (error: unknown) => {
            const isAutomatic = consumePendingAutomaticRefetch(serializedQueryKey);
            if (isAutomatic && !isRefreshCancellation(error)) {
                consecutiveFailuresRef.current += 1;
                setConsecutiveAutomaticFailures(consecutiveFailuresRef.current);
            }

            if (isRefreshCancellation(error)) {
                return;
            }

            // O cache mantém o último dado após o erro, inclusive quando a chave foi
            // reaberta do cache sem onSuccess; por isso não usamos ref nem query.data.
            if (
                isAutomatic &&
                !shouldNotifyPerformanceTotalizersPreserveExpired(
                    consecutiveFailuresRef.current,
                    queryClient.getQueryData<PerformanceTotalizer[]>(queryKey)
                )
            ) {
                return;
            }

            const axiosError = error as {
                response?: { data?: { message?: string; detail?: string } };
                message?: string;
            };
            const errorMessage =
                axiosError?.response?.data?.message ||
                axiosError?.response?.data?.detail ||
                axiosError?.message ||
                "Erro ao carregar totalizadores de performance";
            enqueueSnackbar(errorMessage, {
                variant: "error",
                autoHideDuration: 3000,
            });
        },
    });

    const hasValidSnapshotForCurrentKey =
        hasValidPerformanceTotalizersSnapshot(query.data) && !query.isPreviousData;

    return {
        ...query,
        consecutiveAutomaticFailures,
        preserveExpired: isPerformanceTotalizersPreserveExpired(consecutiveAutomaticFailures),
        hasValidSnapshot: hasValidSnapshotForCurrentKey,
        isEmptySuccess: query.isSuccess && Array.isArray(query.data) && query.data.length === 0,
    };
};
